/**
 * Maps a :class:`MetaCampaignDetailResponse` into the shared detail-page view models
 * (:class:`CampaignInfoField[]` / :class:`ApprovalStep[]`) the cross-platform presentational
 * components consume (#4288 Phase 3b).
 *
 * Lives next to the Meta panel rather than in :file:`components/campaign/detail/` so the
 * Meta-specific gating logic + currency-symbol resolution + mutations stay in one place. The
 * Google adapter (Phase 3b.2) sits next to its own panel for the same reason.
 *
 * The pattern lets the panel stay platform-thin: one query + one adapter call + one render of the
 * shared components. The platform-specific branching that used to live across two parallel detail
 * pages collapses into these adapters.
 */

import { humanizeMetaEnum } from '@/components/campaign/meta/meta-metrics';
import type { CampaignInfoRow } from '@/components/campaign/detail/info-form';
import type React from 'react';

import { useAtom } from 'jotai';

import { usePaymentGate } from '@/hooks/usePaymentGate';
import { useToast } from '@/hooks/useToast';
import {
  useUpdateMetaAdSetBudgetMutation,
  useUpdateMetaCampaignBiddingMutation,
  useUpdateMetaCampaignBudgetMutation,
  useUpdateMetaCampaignStatusMutation,
} from '@/lib/query-options';
import {
  metaAudienceDraftAtom,
  metaBudgetDraftAtom,
  metaInterestsDraftAtom,
  metaLocationsDraftAtom,
  metaPlacementsDraftAtom,
} from '@/store';
import {
  META_CAMPAIGN_TYPE_LABEL,
  META_OBJECTIVE_LABEL,
  META_PLACEMENT_CATALOG,
  metaCampaignDisplayName,
  type CampaignProximityInput,
  type Location,
  type MetaAudienceSelection,
  type MetaBidStrategy,
  type MetaCampaignBiddingUpdateRequest,
  type MetaCampaignDetailResponse,
  type MetaCustomAudienceRef,
  type MetaPlacementSelection,
  type MetaCampaignTargetingUpdateRequest,
} from '@/lib/types';

/** Build the read-only info-form fields for a Meta campaign. Pure transform — no hooks. */
export function metaCampaignInfoFields(campaign: MetaCampaignDetailResponse): CampaignInfoRow[] {
  const phaseLabel = META_CAMPAIGN_TYPE_LABEL[campaign.campaign_type] ?? campaign.campaign_type;
  const createdAtLabel = new Date(campaign.created_at).toLocaleDateString(undefined, {
    day: 'numeric',
    year: 'numeric',
    month: 'short',
  });
  return [
    { label: 'Meta Campaign Name', value: metaCampaignDisplayName(campaign.name, campaign.campaign_type) },
    // One grid row, mirroring Google's "Website URL | Campaign Type | Created On" line (#5981).
    // "Landing Page URL" is the display label (Jeff's naming, #6214); the API field stays offer_url.
    [
      { label: 'Landing Page URL', value: campaign.offer_url },
      { label: 'Campaign Type', value: phaseLabel },
      { label: 'Created On', value: createdAtLabel },
    ],
    // Daily Budget deliberately absent — Google's Overview has no budget field either; it lives
    // (and is edited) on the Budget tab (#5981).
    // Budget Mode moved to the Budget tab (#6211) — it is now a mutable control (the ABO/CBO
    // switcher), not a static fact, and Jeff's parity review names the Budget tab as its home.
    // Optimization Goal surfaces beside the Objective (#6214) — read from the first ad set, the
    // delivery level that owns it (every set in a campaign shares the goal today).
    [
      { label: 'Status', value: campaign.status },
      { label: 'Objective', value: META_OBJECTIVE_LABEL[campaign.objective] ?? campaign.objective },
      { label: 'Optimization Goal', value: humanizeMetaEnum(campaign.ad_sets[0]?.optimization_goal) },
    ],
    // Google's Overview ends on Tracking Template; Meta's UTM template lives on every ad's
    // destination URL as click-time {{…}} macros — surface it read-only from the first ad (#5981).
    { label: 'Tracking Template', value: trackingTemplateFromAds(campaign), monospace: true },
  ];
}

/** The utm_* template shared by the campaign's ads (Meta macros resolved at click time), or ``—``. */
function trackingTemplateFromAds(campaign: MetaCampaignDetailResponse): string {
  const destination = campaign.ad_sets[0]?.ads[0]?.destination_url;
  if (!destination) return '—';
  try {
    const query = new URL(destination).search.replace(/^\?/, '');
    const utms = query.split('&').filter((param) => param.toLowerCase().startsWith('utm_'));
    // decode so Meta's {{…}} macros read literally, like Google's {campaignid} template does.
    return utms.length > 0 ? decodeURIComponent(utms.join('&')) : '—';
  } catch {
    return '—';
  }
}

/**
 * Compact launch control for the detail page's toolbar (#5981) — Google's Go Live placement.
 *
 * Replaces the full-width three-step approval flow: the same readiness rules (non-empty campaign
 * tree + a real budget on the right level, matching the backend launch gate in launch_service.py)
 * collapse into one enabled/disabled Go Live button, with the unmet checks surfaced visibly beside
 * it (#6163 P1-7 — a disabled button's title tooltip is unreachable, so the reasons render inline).
 */
export function useMetaLaunchControl(
  campaign: MetaCampaignDetailResponse | undefined,
  isStillGenerating: boolean,
): {
  canLaunch: boolean;
  unmetChecks: string[];
  isLaunching: boolean;
  launch: () => void;
  /** Render next to the trigger — the upgrade modal FREE-plan users hit instead of launching. */
  PaymentGate: React.ComponentType;
} {
  const { toast } = useToast();
  const statusMutation = useUpdateMetaCampaignStatusMutation();
  // Same monetization gate as Google's go-live (useGoLive): FREE plans see the upgrade modal,
  // paid plans launch immediately (#5981 parity — Meta launches were previously ungated).
  const { PaymentGate, requirePayment } = usePaymentGate({ source: 'meta_campaign_deploy' });

  if (!campaign || isStillGenerating) {
    return { canLaunch: false, unmetChecks: [], isLaunching: false, launch: () => {}, PaymentGate };
  }

  // Non-empty campaign tree. Meta has no per-ad approval state (Google has ``Ad.state = APPROVED``).
  const hasAdSets = campaign.ad_sets.length > 0;
  const hasAds = campaign.ad_sets.some((adSet) => adSet.ads.length > 0);
  // A real (non-placeholder) daily budget on the right level (#4657): campaigns are created with
  // the budget unset locally, so any positive value means the user explicitly set one.
  const cboCents = campaign.daily_budget_cents ?? 0;
  const aboCents = campaign.ad_sets.reduce((sum, adSet) => sum + (adSet.daily_budget_cents ?? 0), 0);
  const isBudgetSet = campaign.is_cbo ? cboCents > 0 : aboCents > 0;

  const unmetChecks: string[] = [];
  if (!hasAdSets) unmetChecks.push('at least one ad set exists');
  if (!hasAds) unmetChecks.push('at least one ad exists');
  if (!isBudgetSet) unmetChecks.push(campaign.is_cbo ? 'campaign daily budget is set' : 'an ad-set daily budget is set');

  const launch = () => {
    requirePayment(() => {
      statusMutation.mutate(
        { slug: campaign.slug, status: 'ACTIVE' },
        {
          onError: (error) => {
            toast({
              title: 'Could Not Launch',
              description: error instanceof Error ? error.message : 'Please try again.',
              variant: 'destructive',
            });
          },
        },
      );
    });
  };

  return {
    canLaunch: unmetChecks.length === 0 && !statusMutation.isPending,
    unmetChecks,
    isLaunching: statusMutation.isPending,
    launch,
    PaymentGate,
  };
}

/**
 * Pause control for a LIVE (``ACTIVE``) Meta campaign — spend safety (#5742 H1).
 *
 * The approval flow above only drives the DRAFT/PAUSED → ACTIVE direction and hides once the campaign
 * is live, which left no way to stop a spending campaign. This is the reverse: flip an ACTIVE campaign
 * back to ``PAUSED`` (the backend cascades to its ad sets + ads). After pausing, the detail cache
 * invalidates, ``status`` becomes ``PAUSED``, and the approval flow reappears with "Go Live" as the
 * resume path. Uses the same status mutation as launch.
 */
export function useMetaPauseControl(campaign: MetaCampaignDetailResponse | undefined): {
  isPausing: boolean;
  pause: () => void;
} {
  const { toast } = useToast();
  const statusMutation = useUpdateMetaCampaignStatusMutation();

  const pause = () => {
    if (!campaign) return;
    statusMutation.mutate(
      { slug: campaign.slug, status: 'PAUSED' },
      {
        onError: (error) => {
          toast({
            title: 'Could Not Pause',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return { isPausing: statusMutation.isPending, pause };
}


/**
 * The Budget tab's staged edits → the toolbar's Save Changes (#5981), mirroring Google's draft +
 * toolbar-save flow. Reads the campaign-keyed draft atom, reports dirtiness, and commits the
 * touched slices in order: daily budget (CBO campaign / sole ABO ad set), then bidding
 * (strategy/cap pushed to Meta, target CPL local). The draft clears on full success and survives
 * a partial failure so a retry re-sends only what remains.
 */
export function useMetaBudgetDraftSave(campaign: MetaCampaignDetailResponse | undefined): {
  isDirty: boolean;
  isSaving: boolean;
  /** Resolves true when everything staged persisted; false on validation failure or a server
   *  error (both toasted). Callers that gate navigation on the save must check it. */
  save: () => Promise<boolean>;
} {
  const { toast } = useToast();
  const [draft, setDraft] = useAtom(metaBudgetDraftAtom);
  const campaignBudget = useUpdateMetaCampaignBudgetMutation();
  const adSetBudget = useUpdateMetaAdSetBudgetMutation();
  const bidding = useUpdateMetaCampaignBiddingMutation();
  const isSaving = campaignBudget.isPending || adSetBudget.isPending || bidding.isPending;

  if (!campaign || draft == null || draft.campaignId !== campaign.id) {
    return { isDirty: false, isSaving, save: async () => true };
  }

  const soleAdSet = !campaign.is_cbo && campaign.ad_sets.length === 1 ? campaign.ad_sets[0] : null;
  const savedCents = campaign.is_cbo ? (campaign.daily_budget_cents ?? 0) : (soleAdSet?.daily_budget_cents ?? 0);
  const savedBudgetWhole = Math.round(savedCents / 100);
  const savedStrategy: MetaBidStrategy = campaign.bid_strategy ?? 'LOWEST_COST_WITHOUT_CAP';
  const savedCapText = campaign.bid_amount_cents != null ? String(campaign.bid_amount_cents / 100) : '';
  const savedCplText = campaign.target_cpl_cents != null ? String(campaign.target_cpl_cents / 100) : '';

  const budgetDirty = draft.budgetWhole != null && draft.budgetWhole !== savedBudgetWhole;
  const strategy = draft.bidStrategy ?? savedStrategy;
  const capText = draft.capText ?? savedCapText;
  const cplText = draft.cplText ?? savedCplText;
  const strategyDirty = strategy !== savedStrategy;
  const capDirty = strategy === 'COST_CAP' && capText !== savedCapText;
  const cplDirty = cplText !== savedCplText;
  const isDirty = budgetDirty || strategyDirty || capDirty || cplDirty;

  const save = async (): Promise<boolean> => {
    const capCents = Math.round((parseFloat(capText) || 0) * 100);
    const cplCents = Math.round((parseFloat(cplText) || 0) * 100);
    if ((strategyDirty || capDirty) && strategy === 'COST_CAP' && capCents <= 0) {
      toast({
        title: 'Could Not Save Changes',
        description: 'Cost Cap requires a cap amount.',
        variant: 'destructive',
      });
      return false;
    }
    try {
      if (budgetDirty) {
        const cents = (draft.budgetWhole ?? 0) * 100;
        if (campaign.is_cbo) {
          await campaignBudget.mutateAsync({ campaignId: campaign.id, daily_budget_cents: cents });
        } else if (soleAdSet) {
          await adSetBudget.mutateAsync({ adSetId: soleAdSet.id, daily_budget_cents: cents });
        }
      }
      if (strategyDirty || capDirty || cplDirty) {
        const body: MetaCampaignBiddingUpdateRequest = {};
        if (strategyDirty || capDirty) {
          body.bid_strategy = strategy;
          if (strategy === 'COST_CAP') body.bid_amount_cents = capCents;
        }
        if (cplDirty) body.target_cpl_cents = cplCents > 0 ? cplCents : null;
        await bidding.mutateAsync({ slug: campaign.slug, body });
      }
      setDraft(null);
      return true;
    } catch (error) {
      toast({
        title: 'Could Not Save Changes',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };

  return { isDirty, isSaving, save };
}

/**
 * One staged targeting slice (interests | placements | audience) for the toolbar's Save Changes.
 * The panel merges every dirty slice's ``bodyFragment`` into ONE targeting PATCH (#6179 — the
 * endpoint applies each provided field in a single write; the previous per-slice saves fired up
 * to three sequential PATCHes). ``validationError`` blocks the combined save with a visible
 * message when a dirty slice cannot produce a valid fragment.
 */
export interface MetaTargetingDraftSlice {
  isDirty: boolean;
  validationError: string | null;
  bodyFragment: Partial<MetaCampaignTargetingUpdateRequest> | null;
  clearDraft: () => void;
}

/**
 * The Targeting → Interests staged edits → the toolbar's Save Changes (#6076 followup). Reads the
 * campaign-keyed draft atom and reports its slice of the combined targeting PATCH.
 */
export function useMetaInterestsDraftSave(campaign: MetaCampaignDetailResponse | undefined): MetaTargetingDraftSlice {
  const [draft, setDraft] = useAtom(metaInterestsDraftAtom);

  if (!campaign || draft == null || draft.campaignId !== campaign.id) {
    return { isDirty: false, validationError: null, bodyFragment: null, clearDraft: () => {} };
  }

  const savedIds = new Set((campaign.target_interests ?? []).map((entry) => entry.id));
  const isDirty =
    draft.interests.length !== savedIds.size || draft.interests.some((entry) => !savedIds.has(entry.id));

  return {
    isDirty,
    validationError: null,
    bodyFragment: isDirty ? { interests: draft.interests } : null,
    clearDraft: () => setDraft(null),
  };
}

/** Placements: saved selection → the tree's checked state (all-checked = Advantage+ automatic) (#6076). */
export function derivePlacementsChecked(saved: MetaPlacementSelection | null): {
  checkedPlatforms: Set<string>;
  checkedPositions: Record<string, Set<string>>;
} {
  const isAutomatic = !saved || saved.publisher_platforms.length === 0;
  const platforms = new Set(isAutomatic ? META_PLACEMENT_CATALOG.map((p) => p.platform) : saved!.publisher_platforms);
  const positions: Record<string, Set<string>> = {};
  for (const entry of META_PLACEMENT_CATALOG) {
    if (!platforms.has(entry.platform)) {
      positions[entry.platform] = new Set();
      continue;
    }
    const narrowed = !isAutomatic ? saved!.positions[entry.platform] : undefined;
    positions[entry.platform] = new Set(narrowed ?? entry.positions.map((pos) => pos.value));
  }
  return { checkedPlatforms: platforms, checkedPositions: positions };
}

/**
 * The tree's checked state → a placement selection. Everything checked with no narrowing collapses to
 * Advantage+ (empty publisher_platforms). Returns null if invalid (no platforms, or a checked platform
 * with no positions) so the caller can reject the save.
 */
function computePlacementsSelection(
  checkedPlatforms: Set<string>,
  checkedPositions: Record<string, Set<string>>,
): MetaPlacementSelection | null {
  const allPlatforms = META_PLACEMENT_CATALOG.map((entry) => entry.platform);
  const platforms = allPlatforms.filter((platform) => checkedPlatforms.has(platform));
  if (platforms.length === 0) return null;
  const positions: Record<string, string[]> = {};
  let anyNarrowing = false;
  for (const entry of META_PLACEMENT_CATALOG) {
    if (!platforms.includes(entry.platform) || entry.positions.length === 0) continue;
    const checked = entry.positions.filter((pos) => (checkedPositions[entry.platform] ?? new Set()).has(pos.value));
    if (checked.length === 0) return null;
    if (checked.length < entry.positions.length) {
      positions[entry.platform] = checked.map((pos) => pos.value);
      anyNarrowing = true;
    }
  }
  const isEverything = platforms.length === allPlatforms.length && !anyNarrowing;
  return isEverything ? { publisher_platforms: [], positions: {} } : { publisher_platforms: platforms, positions };
}

/** Compare two placement selections (platform set + per-platform position set) for equality. Comparing
 *  the computed selection, not the raw checked map, means remembered-but-hidden positions of an
 *  unchecked platform don't count as a change (#6175 followup). */
function placementSelectionsEqual(a: MetaPlacementSelection, b: MetaPlacementSelection): boolean {
  const aPlatforms = new Set(a.publisher_platforms);
  const bPlatforms = new Set(b.publisher_platforms);
  if (aPlatforms.size !== bPlatforms.size || [...aPlatforms].some((p) => !bPlatforms.has(p))) return false;
  const platforms = new Set([...Object.keys(a.positions), ...Object.keys(b.positions)]);
  for (const platform of platforms) {
    const av = new Set(a.positions[platform] ?? []);
    const bv = new Set(b.positions[platform] ?? []);
    if (av.size !== bv.size || [...av].some((pos) => !bv.has(pos))) return false;
  }
  return true;
}

/**
 * Targeting → Placements staged edits → the toolbar's Save Changes (#6076 followup). Reads the
 * campaign-keyed draft (checked state), reports dirtiness vs the saved selection, and commits the
 * converted placement selection in one write. Mirrors useMetaInterestsDraftSave.
 */
export function useMetaPlacementsDraftSave(campaign: MetaCampaignDetailResponse | undefined): MetaTargetingDraftSlice {
  const [draft, setDraft] = useAtom(metaPlacementsDraftAtom);

  if (!campaign || draft == null || draft.campaignId !== campaign.id) {
    return { isDirty: false, validationError: null, bodyFragment: null, clearDraft: () => {} };
  }

  const draftPlatforms = new Set(draft.checkedPlatforms);
  const draftPositions: Record<string, Set<string>> = {};
  for (const [platform, positions] of Object.entries(draft.checkedPositions)) {
    draftPositions[platform] = new Set(positions);
  }
  // Dirty vs the saved selection by comparing computed selections: an invalid draft (no platforms,
  // or a checked platform with no positions) still counts as a change so Save Changes surfaces.
  const savedSelection = campaign.target_placements ?? { publisher_platforms: [], positions: {} };
  const draftSelection = computePlacementsSelection(draftPlatforms, draftPositions);
  const isDirty = draftSelection === null || !placementSelectionsEqual(draftSelection, savedSelection);

  return {
    isDirty,
    validationError:
      isDirty && draftSelection === null
        ? 'Select at least one platform, and at least one placement for each checked platform.'
        : null,
    bodyFragment: isDirty && draftSelection !== null ? { placements: draftSelection } : null,
    clearDraft: () => setDraft(null),
  };
}

/** Canonical form of an audience slice, so undefined/null/empty differences don't read as changes. */
function normalizeAudience(audience: MetaAudienceSelection | null): {
  ageMin: number | null;
  ageMax: number | null;
  genders: string;
  audiences: string;
} {
  const genders = audience?.genders ?? [];
  const refs = audience?.custom_audiences ?? [];
  return {
    ageMin: audience?.age_min ?? null,
    ageMax: audience?.age_max ?? null,
    genders: genders.length === 0 ? '' : [...genders].sort((a, b) => a - b).join(','),
    audiences: refs.length === 0 ? '' : refs.map((ref: MetaCustomAudienceRef) => ref.id).sort().join(','),
  };
}

/** Compare two audience slices (age range, gender, saved audiences) for equality. */
function audienceSelectionEqual(a: MetaAudienceSelection, b: MetaAudienceSelection | null): boolean {
  const na = normalizeAudience(a);
  const nb = normalizeAudience(b);
  return na.ageMin === nb.ageMin && na.ageMax === nb.ageMax && na.genders === nb.genders && na.audiences === nb.audiences;
}

/**
 * The Targeting → Audience staged edits → the toolbar's Save Changes (#6076 followup). Reads the
 * campaign-keyed draft (the whole demographic slice), reports dirtiness vs the saved audience, and
 * commits it in one targeting write (clearing the draft on success). Mirrors useMetaInterestsDraftSave.
 */
export function useMetaAudienceDraftSave(campaign: MetaCampaignDetailResponse | undefined): MetaTargetingDraftSlice {
  const [draft, setDraft] = useAtom(metaAudienceDraftAtom);

  if (!campaign || draft == null || draft.campaignId !== campaign.id) {
    return { isDirty: false, validationError: null, bodyFragment: null, clearDraft: () => {} };
  }

  const isDirty = !audienceSelectionEqual(draft.audience, campaign.target_audience ?? null);

  return {
    isDirty,
    validationError: null,
    bodyFragment: isDirty ? { audience: draft.audience } : null,
    clearDraft: () => setDraft(null),
  };
}

/** Compare two radius lists (lat/lng/radius/address) for equality, order-independent. */
function radiiEqual(a: CampaignProximityInput[], b: CampaignProximityInput[]): boolean {
  if (a.length !== b.length) return false;
  const key = (r: CampaignProximityInput) => `${r.latitude}|${r.longitude}|${r.radius_km}|${r.address ?? ''}`;
  const aKeys = a.map(key).sort();
  const bKeys = b.map(key).sort();
  return aKeys.every((k, i) => k === bKeys[i]);
}

/**
 * The Targeting → Locations staged edits → the toolbar's Save Changes (#6178 followup). Reads the
 * campaign-keyed draft (geo Location rows + radii), reports dirtiness vs the saved selection, and
 * reports its slice of the combined targeting PATCH. The Locations sub-tab's picker edits this
 * draft live and its Done just closes — exactly like Google's CampaignLocationsSection + page
 * save bar. Mirrors useMetaInterestsDraftSave.
 */
export function useMetaLocationsDraftSave(campaign: MetaCampaignDetailResponse | undefined): MetaTargetingDraftSlice {
  const [draft, setDraft] = useAtom(metaLocationsDraftAtom);

  if (!campaign || draft == null || draft.campaignId !== campaign.id) {
    return { isDirty: false, validationError: null, bodyFragment: null, clearDraft: () => {} };
  }

  const saved = new Set(campaign.target_location_criteria_ids ?? []);
  const staged = new Set(draft.locations.map((l: Location) => l.criteria_id));
  const geoDirty = saved.size !== staged.size || [...staged].some((id) => !saved.has(id));
  const isDirty = geoDirty || !radiiEqual(draft.radii, campaign.target_radii ?? []);
  const isEmpty = draft.locations.length === 0 && draft.radii.length === 0;

  return {
    isDirty,
    validationError: isDirty && isEmpty ? 'At least one target location or radius is required.' : null,
    bodyFragment:
      isDirty && !isEmpty
        ? {
            target_location_criteria_ids: draft.locations.map((l: Location) => l.criteria_id),
            target_radii: draft.radii.length > 0 ? draft.radii : null,
          }
        : null,
    clearDraft: () => setDraft(null),
  };
}
